import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Send, Clock, CheckCircle, Building } from "lucide-react";
import { API } from "../apiConfig";


const QuoteRequestPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [form, setForm] = useState({
    business_name: user?.business_name || "",
    business_type: "gallery",
    contact_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    message: "",
    items: []
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        business_name: user.business_name || f.business_name,
        contact_name: user.name || f.contact_name,
        email: user.email || f.email
      }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [productsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/products`, { headers }),
        token ? axios.get(`${API}/quote-requests/my`, { headers }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setProducts(productsRes.data);
      setMyQuotes(quotesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { product_id: "", variant_id: "", quantity: 1 }]
    });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    // Convert "any" back to empty string for variant_id
    if (field === "variant_id" && value === "any") {
      value = "";
    }
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Reset variant when product changes
    if (field === "product_id") {
      newItems[index].variant_id = "";
    }
    
    setForm({ ...form, items: newItems });
  };

  const getProductVariants = (productId) => {
    const product = products.find(p => p.product_id === productId);
    return product?.variants || [];
  };

  const calculateEstimatedTotal = () => {
    return form.items.reduce((total, item) => {
      const product = products.find(p => p.product_id === item.product_id);
      if (!product) return total;
      
      let price = product.price;
      if (item.variant_id && product.variants) {
        const variant = product.variants.find(v => v.variant_id === item.variant_id);
        if (variant) price = variant.price;
      }
      
      return total + (price * (item.quantity || 1));
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.items.length === 0) {
      toast.error("Please add at least one product to your quote request");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, form, { headers });
      toast.success("Quote request submitted! We'll get back to you within 24-48 hours.");
      setForm({
        ...form,
        items: [],
        message: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit quote request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-4xl mx-auto animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded w-1/3" />
            <div className="h-64 bg-muted rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="quote-request-page">
      <Navigation />
      
      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12 text-center"
          >
            <Badge variant="outline" className="mb-4">Trade Enquiries</Badge>
            <h1 className="text-4xl font-medium mb-4">Request a Wholesale Quote</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Tell us about your business and the products you're interested in. 
              Our team will respond with a customized trade pricing offer within 24-48 hours.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-3">
            {/* Quote Form */}
            <div className="lg:col-span-2">
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Quote Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Business Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 md:col-span-1">
                        <Label>Business Name *</Label>
                        <Input
                          value={form.business_name}
                          onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                          placeholder="Your gallery or business name"
                          required
                        />
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <Label>Business Type *</Label>
                        <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gallery">Art Gallery</SelectItem>
                            <SelectItem value="retailer">Retailer</SelectItem>
                            <SelectItem value="interior_design">Interior Design</SelectItem>
                            <SelectItem value="corporate">Corporate</SelectItem>
                            <SelectItem value="hospitality">Hospitality</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Contact Name *</Label>
                        <Input
                          value={form.contact_name}
                          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Phone (optional)</Label>
                        <Input
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="+61..."
                        />
                      </div>
                    </div>

                    {/* Products */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <Label>Products of Interest *</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addItem}>
                          <Plus className="h-4 w-4 mr-1" /> Add Product
                        </Button>
                      </div>
                      
                      {form.items.length === 0 ? (
                        <div className="text-center py-8 border border-dashed rounded-lg">
                          <p className="text-muted-foreground mb-2">No products added yet</p>
                          <Button type="button" variant="outline" size="sm" onClick={addItem}>
                            <Plus className="h-4 w-4 mr-1" /> Add Your First Product
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {form.items.map((item, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 p-3 bg-muted/50 rounded-lg items-end">
                              <div className="col-span-5">
                                <Label className="text-xs">Product</Label>
                                <Select value={item.product_id} onValueChange={(v) => updateItem(idx, "product_id", v)}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {products.map((p) => (
                                      <SelectItem key={p.product_id} value={p.product_id}>
                                        {p.title}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Size</Label>
                                <Select 
                                  value={item.variant_id} 
                                  onValueChange={(v) => updateItem(idx, "variant_id", v)}
                                  disabled={!item.product_id || getProductVariants(item.product_id).length === 0}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Any" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="any">Any size</SelectItem>
                                    {getProductVariants(item.product_id).map((v) => (
                                      <SelectItem key={v.variant_id} value={v.variant_id}>
                                        {v.name} ({v.size})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Quantity</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => updateItem(idx, "quantity", parseInt(e.target.value) || 1)}
                                />
                              </div>
                              <div className="col-span-1">
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Message */}
                    <div>
                      <Label>Additional Notes (optional)</Label>
                      <Textarea
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Tell us about your requirements, timeline, or any specific needs..."
                        rows={4}
                      />
                    </div>

                    <Button type="submit" className="w-full rounded-full" disabled={submitting}>
                      <Send className="h-4 w-4 mr-2" />
                      {submitting ? "Submitting..." : "Submit Quote Request"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Summary & My Quotes */}
            <div className="space-y-6">
              {/* My Previous Quotes */}
              {myQuotes.length > 0 && (
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="text-lg">My Quote Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {myQuotes.slice(0, 5).map((quote) => (
                        <div key={quote.quote_id} className="p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">
                              {quote.items?.length || 0} items
                            </span>
                            <Badge variant={
                              quote.status === "pending" ? "secondary" :
                              quote.status === "responded" ? "default" : "outline"
                            }>
                              {quote.status === "pending" && <Clock className="h-3 w-3 mr-1" />}
                              {quote.status === "responded" && <CheckCircle className="h-3 w-3 mr-1" />}
                              {quote.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(quote.created_at)} • {formatPrice(quote.estimated_total)}
                          </p>
                          {quote.status === "responded" && quote.offered_discount && (
                            <p className="text-sm text-primary mt-2">
                              Offered: {(quote.offered_discount * 100).toFixed(0)}% trade discount
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Info Card */}
              <Card className="rounded-2xl bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <Building className="h-8 w-8 text-primary mb-3" />
                  <h3 className="font-medium mb-2">Trade Benefits</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Exclusive trade pricing</li>
                    <li>• Priority access to new releases</li>
                    <li>• Dedicated account support</li>
                    <li>• Flexible payment terms</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default QuoteRequestPage;
